import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createBillingPortalSession } from '@auw/api-client';
import {
  closeTabIfOpen,
  openExternalInNewTab,
  parseApiError,
  preOpenTabForNavigation,
} from '../utils';

type PortalPurpose = 'manage' | 'cancel' | 'billing';

interface ToastPayload {
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  description?: string;
}

interface UseBillingPortalOptions {
  subscriptionId?: string | null;
  addToast: (toast: ToastPayload) => void;
  errorTitle: string;
  errorFallbackMessage: string;
}

export const useBillingPortal = ({
  subscriptionId,
  addToast,
  errorTitle,
  errorFallbackMessage,
}: UseBillingPortalOptions) => {
  const { t } = useTranslation('settings');
  const [portalLoading, setPortalLoading] = useState(false);

  const openPortal = useCallback(async (purpose: PortalPurpose) => {
    setPortalLoading(true);
    const pendingTab = preOpenTabForNavigation();
    try {
      const result = await createBillingPortalSession(
        subscriptionId || undefined,
        purpose,
      );
      const opened = openExternalInNewTab(result.portal_url, pendingTab);
      if (!opened) {
        addToast({
          type: 'warning',
          title: t('portal.popupBlocked'),
          description: t('portal.popupBlockedDesc'),
        });
      }
    } catch (error) {
      closeTabIfOpen(pendingTab);
      addToast({
        type: 'error',
        title: errorTitle,
        description: parseApiError(error, errorFallbackMessage),
      });
    } finally {
      setPortalLoading(false);
    }
  }, [addToast, errorFallbackMessage, errorTitle, subscriptionId, t]);

  return {
    portalLoading,
    openPortal,
  };
};
